import { Box } from '@mui/material';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { MarketId } from 'types/market-id.enum';
import _ from 'underscore';
import { isArrayEmptyOrNull } from 'utils/empty-or-null';
import { clearState } from 'utils/storage';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { getRecommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/api/get-order';
import { getOrders } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/api/get-orders';
import ChileOrderList from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order-list/chile-order-list';
import StandardOrderList from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order-list/standard-order-list';
import CreateOrder from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order/create-order';
import { OrderListItem } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/order-list-item.type';
import { RecommendedOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/recommendation.type';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { isValidRouteParam } from 'views/order-feed/utils/is-valid-route-param';

type OrderListByMarketProps = {
  isLoading: boolean;
  orders: OrderListItem[];
  recommendedOrders: RecommendedOrder[];
  isNoResults: boolean;
};

const OrderListByMarket: React.FC<OrderListByMarketProps> = (props) => {
  const {
    account: { company },
  } = useAccountState();

  switch (company?.marketId) {
    case MarketId.Chile:
      return <ChileOrderList {...props} />;
    default:
      return <StandardOrderList {...props} />;
  }
};

const OrderList: React.FC = () => {
  const params = useParams<MySupplyPlanParams>();
  const {
    account: { company },
  } = useAccountState();

  const isChileMarket = company?.marketId === MarketId.Chile;

  const {
    sitesQuery: { data: sites },
  } = useSitesState();

  const isSiteOfd1Enabled = sites?.find((site) => site.globalSiteId === params.globalSiteId)?.isOfd1Enabled;

  // #region FETCH DATA

  const recommendationQuery = useGet(
    ['getOrderRecommended', company?.companyId],
    (context) => getRecommendation(String(context.queryKey[1])),
    {
      enabled: isSiteOfd1Enabled && !!company?.features?.hasOrderFreeDelivery && !!company?.companyId,
    }
  );

  const ordersQuery = useGet(
    ['getOrders', company?.customerErpId, params.siteErpId],
    ({ queryKey }) =>
      getOrders(
        String(queryKey[1]), // customerErpId
        String(queryKey[2]) // siteErpId
      ),
    {
      enabled: !!company?.customerErpId && isValidRouteParam(params.siteErpId),
    }
  );

  const { data: recommendationInfo } = recommendationQuery;
  const { data: orders } = ordersQuery;
  const isLoading = ordersQuery.isLoading || ordersQuery.isFetching || recommendationQuery.isLoading || recommendationQuery.isFetching;
  // #endregion FETCH DATA

  // #region RENDERS

  const ordersToRender = useMemo(() => {
    return _.chain(orders)
      .filter((o) => o.siteErpId === params.siteErpId)
      .sortBy((o) => dayjs(isChileMarket ? o.deliveryDate : o.dispatchDate).valueOf())
      .value();
  }, [isChileMarket, orders, params.siteErpId]);

  const recommendedOrdersToRender = useMemo<RecommendedOrder[]>(() => {
    const recommendations =
      recommendationInfo?.recommendations.filter((recommendation) => recommendation.globalSiteId === params.globalSiteId) ?? [];
    const recommendationsByDate = _.groupBy(recommendations, 'date');
    const groupedRecommendations = Object.values(recommendationsByDate).flatMap((recommendationsForDate) =>
      Object.values(_.groupBy(recommendationsForDate, 'scheduleId'))
    );

    return groupedRecommendations
      .flatMap((recommendedOrderLines) => {
        const firstLine = recommendedOrderLines[0];
        if (!firstLine) return [];

        return [
          {
            date: firstLine.date,
            arrivalDate: firstLine.arrivalDate,
            globalSiteId: firstLine.globalSiteId,
            siteErpId: firstLine.siteErpId,
            siteName: firstLine.siteName,
            scheduleId: firstLine.scheduleId,
            recommendedOrderLines,
          },
        ];
      })
      .sort((left, right) => left.date.localeCompare(right.date));
  }, [params.globalSiteId, recommendationInfo?.recommendations]);

  // #endregion RENDERS

  // #region HOOKS

  useEffect(() => {
    return () => clearState({ key: 'createOrderData' });
  }, []);
  // #endregion HOOKS

  const isNoResults = isArrayEmptyOrNull(ordersToRender) && isArrayEmptyOrNull(recommendedOrdersToRender);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <OrderListByMarket
        isLoading={isLoading}
        orders={ordersToRender}
        recommendedOrders={recommendedOrdersToRender}
        isNoResults={isNoResults}
      />
      <CreateOrder orders={orders} />
    </Box>
  );
};

export default OrderList;
